// Cookie management utilities
// 移行対象キー（user_info等）はlocalStorageに「{appName}_{key}」形式で保存される。
// getCookie/setCookie/removeCookieのインターフェースは変更なし。

import { appStorage } from './appStorage';
import logger from './logger';

// localStorageに移行するキー（アプリ間で競合するデータ）
const LOCAL_STORAGE_KEYS = new Set([
  'user_info',
  'course_id',
  'course',
  'chapter_id',
  'chapter',
  'vocabulary_id',
  'vocabulary',
]);

// アプリ名プレフィックスを取得（sessionStorage → URLパラメータのフォールバック付き）
function getAppPrefix() {
  if (typeof window === 'undefined') return null;
  const appName = appStorage.getAppName()
    || new URLSearchParams(window.location.search).get('app');
  return appName ? appName.toLowerCase() : null;
}

export const cookieManager = {
  // Set a cookie (移行対象キーはlocalStorageに保存)
  setCookie(name, value, days = 7) {
    if (LOCAL_STORAGE_KEYS.has(name)) {
      const prefix = getAppPrefix();
      if (!prefix) {
        logger.warn(`[cookieManager] setCookie: appName未設定のため ${name} を保存できません`);
        return;
      }
      try {
        localStorage.setItem(`${prefix}_${name}`, value);
      } catch (e) {
        logger.error(`[localStorage SET ERROR] ${prefix}_${name}:`, e);
      }
      return;
    }

    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    
    try {
      const encodedValue = encodeURIComponent(value);
      const cookieString = `${name}=${encodedValue};expires=${expires.toUTCString()};path=/`;
      document.cookie = cookieString;
    } catch (e) {
      logger.error(`[Cookie SET ERROR] ${name}:`, e);
    }
  },

  // Get a cookie value (移行対象キーはlocalStorageから読む)
  getCookie(name) {
    if (LOCAL_STORAGE_KEYS.has(name)) {
      const prefix = getAppPrefix();
      if (!prefix) {
        logger.warn(`[cookieManager] getCookie: appName未設定のため ${name} を取得できません`);
        return null;
      }
      try {
        return localStorage.getItem(`${prefix}_${name}`) || null;
      } catch (e) {
        logger.error(`[localStorage GET ERROR] ${prefix}_${name}:`, e);
        return null;
      }
    }

    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      
      if (c.indexOf(nameEQ) === 0) {
        const encodedValue = c.substring(nameEQ.length, c.length);
        
        try {
          const decodedValue = decodeURIComponent(encodedValue);
          return decodedValue;
        } catch (e) {
          logger.error(`[Cookie GET DECODE ERROR] ${name}:`, e);
          return encodedValue;
        }
      }
    }
    
    return null;
  },

  // Remove a cookie (移行対象キーはlocalStorageから削除)
  removeCookie(name) {
    if (LOCAL_STORAGE_KEYS.has(name)) {
      const prefix = getAppPrefix();
      if (!prefix) {
        logger.warn(`[cookieManager] removeCookie: appName未設定のため ${name} を削除できません`);
        return;
      }
      localStorage.removeItem(`${prefix}_${name}`);
      return;
    }

    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  },

  // Check if a cookie exists
  hasCookie(name) {
    return this.getCookie(name) !== null;
  },

  // Clear all cookies
  // 注意: 移行対象キー（user_info, course_id等）はlocalStorageに保存されるため、
  // このメソッドでは削除されない。全データ削除はauthManager.clearAllData()を使うこと。
  clearAllCookies() {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      this.removeCookie(name);
    }
  }
};

export const CookieName = {
  // AppId and AppTitle are now in sessionStorage via appStorage.js (tab-isolated)
  UserEmail: 'user_email',
  UserInfo: 'user_info',
  CourseId: 'course_id',
  Course: 'course',
  ChapterId: 'chapter_id',
  Chapter: 'chapter',
  VocabularyId: 'vocabulary_id',
  Vocabulary: 'vocabulary',
  DeviceId: 'device_id',
};
